import React, { useState } from "react";
import {
  ArrowRight,
  BarChart3,
  DollarSign,
  Mic,
  Phone,
  Sparkles,
  Users,
} from "lucide-react";
import SectionHeading from "../SectionHeading/SectionHeading";

/**
 * Coaching ROI estimator — the page's ROI section. Owns its own inputs, the
 * derived maths, and its markup.
 *
 *   annualCalls = reps × callsPerWeek × WEEKS_PER_YEAR
 *   closedDeals = annualCalls × CLOSE_RATE
 *   recovered = round(closedDeals × ticket × RECOVERY_RATE)
 *
 * At the defaults (8 / 12 / $4,000) that is 4,800 conversations, 1,440 closed
 * deals and $230,400 recovered — the figure the page is checked against.
 *
 * The three rates are fixed marketing assumptions supplied by the business.
 */
const WEEKS_PER_YEAR = 50;
const CLOSE_RATE = 0.3;
const RECOVERY_RATE = 0.04;

const BOUNDS = {
  reps: { min: 1, max: 50, step: 1, default: 8 },
  callsPerWeek: { min: 1, max: 30, step: 1, default: 12 },
  ticket: { min: 500, max: 20000, step: 100, default: 4000 },
};

/**
 * The filled portion of each slider track follows the thumb, so it has to be an
 * inline gradient rather than a class. This is the one place a brand color is
 * repeated as a literal — `theme()` is build-time only, unreachable from JS.
 * Keep in sync with `primary` in tailwind.config.js.
 */
const SLIDER_FILL = "#B21E34";
const SLIDER_TRACK = "rgba(255,255,255,0.15)";

const count = (value: number): string =>
  Math.round(value).toLocaleString("en-US");

const money = (value: number): string => `$${count(value)}`;

const percentOf = (value: number, min: number, max: number): number =>
  max === min ? 0 : ((value - min) / (max - min)) * 100;

interface ROIEstimatorBlockProps {
  /** Anchor the CTA scrolls to. */
  formSectionId: string;
}

const ROIEstimatorBlock: React.FC<ROIEstimatorBlockProps> = ({
  formSectionId,
}) => {
  const [reps, setReps] = useState(BOUNDS.reps.default);
  const [callsPerWeek, setCallsPerWeek] = useState(BOUNDS.callsPerWeek.default);
  const [ticket, setTicket] = useState(BOUNDS.ticket.default);

  const annualCalls = reps * callsPerWeek * WEEKS_PER_YEAR;
  const closedDeals = annualCalls * CLOSE_RATE;
  const recovered = Math.round(closedDeals * ticket * RECOVERY_RATE);

  const closePercent = CLOSE_RATE * 100;
  const recoveryPercent = RECOVERY_RATE * 100;

  const sliders = [
    {
      label: "Field Reps",
      icon: <Users className="h-4 w-4 text-gold" />,
      display: String(reps),
      value: reps,
      onChange: setReps,
      bounds: BOUNDS.reps,
      minLabel: String(BOUNDS.reps.min),
      maxLabel: String(BOUNDS.reps.max),
      helper: undefined as string | undefined,
    },
    {
      label: "Calls per Rep / Week",
      icon: <Mic className="h-4 w-4 text-gold" />,
      display: String(callsPerWeek),
      value: callsPerWeek,
      onChange: setCallsPerWeek,
      bounds: BOUNDS.callsPerWeek,
      minLabel: String(BOUNDS.callsPerWeek.min),
      maxLabel: String(BOUNDS.callsPerWeek.max),
      helper: undefined as string | undefined,
    },
    {
      label: "Average Ticket Value",
      icon: <DollarSign className="h-4 w-4 text-[#3FB37F]" />,
      display: money(ticket),
      value: ticket,
      onChange: setTicket,
      bounds: BOUNDS.ticket,
      minLabel: "$500",
      maxLabel: "$20,000",
      helper: "What's your typical ticket value?",
    },
  ];

  return (
    <section className="bg-mist py-20 dark:bg-slate-950" id="roi-estimator">
      <style>{`
        .fc-roi-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #3FA9E0;
          border: 3px solid #fff;
          cursor: pointer;
          box-shadow: 0 4px 12px -2px rgba(0,0,0,.4);
          transition: transform .15s ease;
        }
        .fc-roi-slider::-webkit-slider-thumb:hover { transform: scale(1.1); }
        .fc-roi-slider::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #3FA9E0;
          border: 3px solid #fff;
          cursor: pointer;
          box-shadow: 0 4px 12px -2px rgba(0,0,0,.4);
        }
      `}</style>

      <div className="mx-auto max-w-7xl px-8">
        <div className="relative overflow-hidden rounded-[1.75rem] bg-gradient-to-br from-[#0A0226] via-secondary to-[#3A0E5C] px-6 py-12 shadow-2xl md:px-12 md:py-16">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(45%_50%_at_85%_15%,rgba(178,0,48,.20),transparent_60%),radial-gradient(35%_40%_at_5%_90%,rgba(255,200,87,.08),transparent_60%)]"
          />

          <div className="relative z-10">
            <div className="mx-auto mb-12 max-w-3xl text-center">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-sm font-semibold text-white">
                <Sparkles className="h-4 w-4 text-gold" />
                Coaching ROI Estimator
              </div>
              <SectionHeading onDark>
                Estimate Your Recoverable Revenue
              </SectionHeading>
              <p className="mt-4 text-base font-light leading-relaxed text-white/70">
                Across the industry, sales teams typically leak ~
                {recoveryPercent}% of revenue through small habits — shortened
                discovery, creeping discounts, and weak closes. Use the
                estimator to see what disciplined coaching could recover for
                your team.
              </p>
            </div>

            <div className="grid grid-cols-1 items-start gap-7 lg:grid-cols-[1fr_1.15fr]">
              {/* Inputs */}
              <div className="flex flex-col gap-4">
                {sliders.map((slider) => {
                  const percent = percentOf(
                    slider.value,
                    slider.bounds.min,
                    slider.bounds.max,
                  );

                  return (
                    <div
                      className="rounded-2xl border border-white/10 bg-white/5 px-6 py-5"
                      key={slider.label}
                    >
                      <div className="mb-3.5 flex items-center justify-between gap-3.5">
                        <label
                          className="flex items-center gap-2.5 text-[0.95rem] font-bold text-white"
                          htmlFor={`roi-${slider.label}`}
                        >
                          {slider.icon}
                          {slider.label}
                        </label>
                        <div className="text-[1.35rem] font-extrabold tracking-tight text-white">
                          {slider.display}
                        </div>
                      </div>

                      <input
                        className="fc-roi-slider h-1.5 w-full cursor-pointer appearance-none rounded-full outline-none"
                        id={`roi-${slider.label}`}
                        max={slider.bounds.max}
                        min={slider.bounds.min}
                        onChange={(event) =>
                          slider.onChange(Number(event.target.value))
                        }
                        step={slider.bounds.step}
                        style={{
                          background: `linear-gradient(90deg, ${SLIDER_FILL} ${percent}%, ${SLIDER_TRACK} ${percent}%)`,
                        }}
                        type="range"
                        value={slider.value}
                      />

                      <div className="mt-2 flex justify-between text-[0.7rem] tracking-wide text-white/45">
                        <span>{slider.minLabel}</span>
                        <span>{slider.maxLabel}</span>
                      </div>

                      {slider.helper && (
                        <p className="mt-3.5 text-center text-xs italic text-white/50">
                          {slider.helper}
                        </p>
                      )}
                    </div>
                  );
                })}

                <div className="flex items-start gap-3.5 rounded-2xl border border-primary/35 bg-primary/15 px-6 py-5">
                  <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-gold/20 text-gold">
                    <BarChart3 className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="mb-1 block text-xs font-medium text-white/65">
                      Industry Benchmark
                    </span>
                    <h4 className="text-base font-extrabold leading-tight text-white">
                      ~{recoveryPercent}% of revenue is typically lost to
                      coaching gaps
                    </h4>
                    <p className="mt-2 text-xs leading-relaxed text-white/55">
                      An industry-wide pattern: small habits — incomplete
                      discovery, creeping discounts, weakened closes — quietly
                      erode roughly {recoveryPercent}% of sales revenue.
                      Disciplined coaching is the proven way to recover it.
                    </p>
                  </div>
                </div>
              </div>

              {/* Result */}
              <div className="rounded-[1.25rem] bg-white p-8 text-center shadow-2xl dark:bg-slate-800">
                <div className="text-base font-medium text-gray-500 dark:text-gray-400">
                  Estimated Annual Revenue Recovered
                </div>
                <div className="my-3.5 text-5xl font-extrabold leading-none tracking-tight text-primary dark:text-primary-light md:text-6xl">
                  {money(recovered)}
                </div>
                <div className="text-lg font-medium text-gray-900 dark:text-white">
                  from{" "}
                  <strong className="font-extrabold text-primary dark:text-primary-light">
                    {count(annualCalls)} coachable conversations
                  </strong>
                </div>

                <div className="mt-7 rounded-2xl bg-mist px-6 py-5 dark:bg-slate-700">
                  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3.5">
                    <div className="text-center">
                      <div className="mb-1.5 text-sm font-medium text-gray-500 dark:text-gray-400">
                        Annual Conversations
                      </div>
                      <div className="text-2xl font-extrabold leading-none tracking-tight text-gray-900 dark:text-white">
                        {count(annualCalls)}
                      </div>
                    </div>
                    <ArrowRight className="h-6 w-6 text-gray-300 dark:text-gray-500" />
                    <div className="text-center">
                      <div className="mb-1.5 text-sm font-medium text-gray-500 dark:text-gray-400">
                        Closed Deals
                      </div>
                      <div className="text-2xl font-extrabold leading-none tracking-tight text-primary dark:text-primary-light">
                        {count(closedDeals)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 border-t border-gray-200 pt-4 text-sm leading-relaxed text-gray-500 dark:border-slate-600 dark:text-gray-400">
                    <strong className="font-bold text-gray-900 dark:text-white">
                      {count(annualCalls)} calls
                    </strong>{" "}
                    × ~{closePercent}% close ×{" "}
                    <strong className="font-bold text-gray-900 dark:text-white">
                      {money(ticket)}
                    </strong>{" "}
                    avg ticket × ~{recoveryPercent}% coaching recovery
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-[#A7F3D0] bg-[#ECFDF5] px-5 py-4 dark:border-emerald-800 dark:bg-emerald-900/20">
                  <div className="text-sm font-semibold text-[#047857] dark:text-emerald-400">
                    Estimated 12-Month Revenue Recovery
                  </div>
                  <div className="mt-1 text-2xl font-extrabold leading-none tracking-tight text-[#065F46] dark:text-emerald-300 md:text-3xl">
                    {money(recovered)}
                  </div>
                </div>

                <a
                  className="mt-5 flex w-full items-center justify-center gap-2.5 rounded-xl bg-primary px-5 py-4 text-base font-bold text-white transition-all duration-200 hover:-translate-y-px hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary"
                  href={`#${formSectionId}`}
                >
                  <Phone className="h-4 w-4" />
                  Get Your Personalized ROI Analysis
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROIEstimatorBlock;
